import { memo, useCallback, useEffect, useState } from 'react';
import { Box, Button, Flex, useMediaQuery } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { FetchModelBlocks, getBlocks, setActiveOverviewTab } from 'redux/ModelsSlice';
import GridMenuIcon from 'components/Icons/GridMenuIcon';
import { UserModalNew } from 'pages/UserManagement/ProfileNewUIRevamp';
// The revamped app header — the same components the Blocks panel renders from
// (`ModalOverViewPageHeaderRevamp/Header.tsx`). This file previously imported
// the equivalents from `ModalOverViewPageHeaderV1`, so navigating from Blocks
// into a block's plan swapped the whole top bar for the older one: different
// height, different logo treatment, different nav pills. Same module now, so
// the bar is literally the same component tree on both routes.
import LogoSection from 'pages/ModalOverViewPageHeaderRevamp/components/LogoSection';
import NavigationBar from 'pages/ModalOverViewPageHeaderRevamp/components/NavigationBar';
import UserActions from 'pages/ModalOverViewPageHeaderRevamp/components/UserActions';
import MobileNavigationMenu from 'pages/ModalOverViewPageHeaderRevamp/components/MobileNavigationMenu';
import { HEADER_CONFIG, getUrlValueFromTab } from 'pages/ModalOverViewPageHeaderRevamp/constants';

import type { TabType } from 'pages/ModalOverViewPageHeaderRevamp/types';

const NewHeader = () => {
  const { modelId } = useParams();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const model = useSelector(getBlocks);
  const [isPhone] = useMediaQuery('(max-width: 767px)');
  const [isIpad] = useMediaQuery('(min-width: 768px) and (max-width: 1024px)');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (modelId && Number(model?.id) !== Number(modelId)) {
      dispatch(FetchModelBlocks({ modelId: Number(modelId) }));
    }
  }, [dispatch, modelId, model?.id]);

  // The plan page lives on /model/:modelId/block/:blockId/plan, so tab clicks
  // navigate back to the model overview route instead of swapping local tabs.
  const handleTabChange = useCallback(
    (tab: TabType) => {
      dispatch(setActiveOverviewTab(tab));
      if (modelId) {
        navigate(`/model/${modelId}/${getUrlValueFromTab(tab)}`);
      }
    },
    [dispatch, modelId, navigate],
  );

  const showTabletMenu = Boolean(isPhone);
  const headerGridTemplateColumns: string = showTabletMenu || isIpad ? '1fr auto' : '1fr auto 1fr';

  return (
    <>
      {/* Layout mirrors the Blocks panel's header bar exactly — same padding,
          height, border and 3-column grid — so the two routes' top bars line up
          pixel for pixel rather than merely looking similar. */}
      <Box
        display='flex'
        flexDirection='column'
        w='100%'
        minH={HEADER_CONFIG.HEIGHT}
        flexShrink={0}
        position='relative'
        zIndex={4000}
        px={3}
        py='6px'
        alignItems='center'
        justifyContent='space-between'
        bg={HEADER_CONFIG.BG_COLOR}
        gap='6px'
        borderBottom='1px solid #D1D6DD'
      >
        <Box
          display='grid'
          gridTemplateColumns={headerGridTemplateColumns}
          alignItems='center'
          gap={2}
          width='100%'
        >
          <Box justifySelf='start' minW={0}>
            <LogoSection modelName={model?.name} modelData={model} />
          </Box>
          {!showTabletMenu && !isIpad && (
            <Box display='flex' justifyContent='center'>
              {/* A block's plan page is reached from the Blocks tab, so that tab
                  stays lit while the user is down inside a block. */}
              <NavigationBar activeTab='blocks' onTabChange={handleTabChange} />
            </Box>
          )}
          <Flex alignItems='center' gap={2} justifySelf='end'>
            {showTabletMenu && (
              <Button
                variant='solid'
                bg='white'
                borderRadius='full'
                width='36px'
                height='36px'
                minW='36px'
                p={0}
                _hover={{ bg: 'gray.50' }}
                aria-label='Open navigation menu'
                onClick={() => setIsMenuOpen(true)}
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                <GridMenuIcon width={20} height={20} color='#6B5FC6' />
              </Button>
            )}
            {!showTabletMenu && <UserActions />}
          </Flex>
        </Box>
        {isIpad && (
          <Box display='flex' alignItems='center' justifyContent='center' px={6} pb={2}>
            <NavigationBar activeTab='blocks' onTabChange={handleTabChange} />
          </Box>
        )}
      </Box>

      {showTabletMenu && (
        <MobileNavigationMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          activeTab='blocks'
          onTabChange={handleTabChange}
        />
      )}

      <UserModalNew />
    </>
  );
};

export default memo(NewHeader);
